import type { PrincipalKind } from '@gridfit/core/principals'
import { outboxIsEmpty } from '../outbox/db'

/**
 * How long a device-mode session must sit with no interaction before an
 * update is accepted for it automatically (review §5.6; `sw.ts`'s own top
 * comment: "the app posts SKIP_WAITING ... or when device mode decides it
 * is idle"). Two minutes: long enough that a manager glancing at a kiosk
 * mid-shift is very unlikely to be reading it at the exact moment this
 * fires, short enough that a genuinely unattended device does not sit on a
 * stale build for long once one becomes available.
 *
 * A BEHAVIOUR CONSTANT, same treatment `packages/api/src/worker.ts`'s
 * `belowFloor`/`MIN_CLIENT_BUILD` doc comment gives itself (CLAUDE.md: "no
 * behaviour constants in code — the config registry owns them"): 080's
 * config registry is this value's eventual home; it is a plain exported
 * constant here until that registry exists to own it.
 */
export const IDLE_UPDATE_MS = 120_000

/**
 * How often the device-mode idle check re-evaluates (review round 1,
 * Important 1). A REPEATING check against a `lastInteractionAt` timestamp,
 * not a single `setTimeout` re-armed only by an interaction event, is what
 * lets a BLOCKED attempt (a focused input, a non-empty outbox) retry on its
 * own the NEXT time this interval fires — without waiting for a fresh
 * interaction event to re-arm it, which may never come on a genuinely
 * unattended device (measured: a kiosk with an input focused for over an
 * hour, then blurred with no further touch at all, never got a second
 * chance under the old single-`setTimeout` shape). One second is frequent
 * enough that the retry delay is invisible against `IDLE_UPDATE_MS`'s
 * two-minute scale, and cheap enough to run indefinitely while a device
 * sits idle. Same config-registry-080 treatment as `IDLE_UPDATE_MS` above.
 */
export const IDLE_CHECK_INTERVAL_MS = 1_000

/**
 * Is this session a "device" for update-acceptance purposes (review §5.6,
 * v3-tasks/040: "Kiosk/device mode auto-accepts updates when idle")?
 *
 * `kind` is the whole answer today: a `kiosk` principal is architecture
 * §7.2's own device-shaped principal kind (`actorKindOf('kiosk') ===
 * 'device'`, `packages/core/src/principals/index.ts`) — the enrolled wall
 * tablet review §5.6 describes, with no human expected to walk over and
 * click "reload".
 *
 * `pathname` is part of this function's signature (this task's own brief)
 * but is NOT yet load-bearing, and this is disclosed rather than hidden
 * behind a plausible-looking branch that isn't really there: no
 * kiosk-dedicated ROUTE exists in this app yet (task 160 is what adds
 * kiosk clock-in) — a `kiosk` principal today lands on `/no-areas`
 * (`shell/landing.ts`'s `PRIORITY` table has no `clock` entry), the exact
 * same landing spot as any other capability-less principal. Threading
 * `pathname` through now, rather than adding it as a breaking change to
 * this exported function's signature later, gives a future task a stable
 * seam if device mode ever needs to be scoped by ROUTE as well as by
 * principal (e.g. "device mode only while showing this device's own
 * clock-in screen") once such a route exists.
 */
export function isDeviceMode(pathname: string, kind: PrincipalKind): boolean {
  return kind === 'kiosk'
}

/**
 * Is the offline command queue empty right now?
 *
 * WIRED to the real outbox (Task 17, `outbox/db.ts`). `outboxIsEmpty()`
 * mirrors the Dexie `entries` table's row count in a plain module-scope
 * `number | null`, updated synchronously by `enqueue()`/`drain()` as they
 * write — the same "module-scope queue, mutated by enqueue/drain, read
 * synchronously here" shape this comment previously described as the
 * planned shape, now real: IndexedDB has no synchronous read API, so
 * `outboxDb().entries.count()` itself could never back a synchronous
 * function directly.
 *
 * `test/outbox.test.tsx`'s wiring case proves BOTH directions this file's
 * own comment used to say only Task 17/18 could prove: a real, non-empty
 * outbox blocks the kiosk auto-accept effect below (`shell/UpdateBanner.tsx`
 * `attemptAutoAccept`'s `if (!isOutboxEmpty()) return`), and draining it
 * lets that SAME repeating check succeed on its very next tick — with no
 * fresh interaction needed, exactly the retry behaviour Task 16 built this
 * as a repeating `setInterval` (not a one-shot `setTimeout`) to guarantee.
 */
export function isOutboxEmpty(): boolean {
  return outboxIsEmpty()
}
